// import { from } from '@apollo/client'
// import { cn } from '@fullcalendar/core/internal-common'
import cn from 'classnames'
import dynamic from 'next/dynamic'
import React, { memo, useEffect, useState } from 'react'
import { FilterOption, TaxonomyType } from 'src/types'

import { CustomTagFilter } from '../CustomTag/CustomTagFilter'
import SkeletonLoading from '../SkeletonLoading/SkeletonLoading'

// import { Accordion } from '../SidebarItem/Accordion'
// import { DateRangePicker } from '../SidebarItem/DateRangePicker'
// import { SidebarTopicFilter } from '../SidebarTopicFilter/SidebarTopicFilter'
// import { TopicList } from '../TopicList/TopicList'

export interface SidebarMenuItem {
  title: string
  subtitles: string[]
}

export interface SidebarProps {
  className?: string
  filterOptions: FilterOption[]
  filtersDisabled?: boolean
  defaultSelectFilterIdx?: number
  onClickWhenDisabled?: () => void
  filterHeight?: boolean | null
  sharedPortfolioId?: string
  isSharedPortfolio?: boolean
  onCustomTagsChanged?: () => void
}

const SidebarTopicFilter = dynamic(
  () => import('../SidebarTopicFilter/SidebarTopicFilter').then((mod) => mod.SidebarTopicFilter),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const TopicList = dynamic(() => import('../TopicList/TopicList').then((mod) => mod.TopicList), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const DateRangePicker = dynamic(() => import('../SidebarItem/DateRangePicker').then((mod) => mod.DateRangePicker), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const Accordion = dynamic(() => import('../SidebarItem/Accordion').then((mod) => mod.Accordion), {
  loading: () => <SkeletonLoading className="bg-navy" />,
})

export const Sidebar: React.FC<SidebarProps> = memo(
  ({
    className = '',
    filtersDisabled = false,
    defaultSelectFilterIdx = 0,
    filterOptions = [],
    onClickWhenDisabled,
    filterHeight = false,
    sharedPortfolioId,
    isSharedPortfolio = true,
    onCustomTagsChanged,
  }) => {
    const [filterOptionsData, setFilterOptionsData] = useState<FilterOption[]>([])
    const [selectedAccordion, setSelectedAccordion] = useState<TaxonomyType | null>(null)
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    const onSelectAccordion = (title: TaxonomyType) => {
      if (title === selectedAccordion) {
        setSelectedAccordion(null)
        // Store null when closing accordion
        if (typeof window !== 'undefined') {
          localStorage.setItem('lastSelectedFilter', 'null')
        }
      } else {
        setSelectedAccordion(title)
        // Store the selected accordion title
        if (typeof window !== 'undefined') {
          localStorage.setItem('lastSelectedFilter', title)
        }
      }
    }

    useEffect(() => {
      setFilterOptionsData([...filterOptions])
    }, [filterOptions])
    useEffect(() => {
      if (filterOptionsData) {
        if (isInitialLoad) {
          // On initial page load, clear localStorage and set default
          if (typeof window !== 'undefined') {
            localStorage.removeItem('lastSelectedFilter')
          }
          setSelectedAccordion(filterOptionsData[defaultSelectFilterIdx]?.title || null)
          setIsInitialLoad(false)
        } else {
          // On subsequent updates, check localStorage for user selections
          if (typeof window !== 'undefined') {
            const lastSelectedFilter = localStorage.getItem('lastSelectedFilter')
            if (lastSelectedFilter && lastSelectedFilter !== 'null') {
              // Check if the stored filter exists in current filter options
              const filterExists = filterOptionsData.some((filter) => filter.title === lastSelectedFilter)
              if (filterExists) {
                setSelectedAccordion(lastSelectedFilter as TaxonomyType)
                return
              }
            }
          }
          // Fallback to default if no valid stored selection
          setSelectedAccordion(filterOptionsData[defaultSelectFilterIdx]?.title || null)
        }
      }
    }, [filterOptionsData, defaultSelectFilterIdx, isInitialLoad])

    return (
      <div
        className={cn(className, 'mobile_filter_scroll')}
        tabIndex={0}
        role="complementary"
        aria-label="Filter sidebar"
      >
        <div className={`md:max-w-[15rem] md:w-[240px] w-full ${filterHeight ? 'hide-filter-free-popup' : ''}`}>
          {filterOptionsData.map((filter: FilterOption, index: number) => {
            const opened = selectedAccordion === filter.title
            // Custom Tags stay visible even with zero tags so users can create tags from the sidebar
            if (
              filter.title !== TaxonomyType.Date &&
              filter.title !== TaxonomyType.CustomTag &&
              (!filter.data || !filter.data.length)
            )
              return
            if (filter.title === TaxonomyType.Subtopic && filter.slug) {
              return (
                <div key={filter?.title}>
                  <hr></hr>
                  <SidebarTopicFilter
                    title={TaxonomyType.Subtopic}
                    topicSlug={filter.slug}
                    items={filter.data}
                    opened={opened}
                    disabled={filtersDisabled}
                    onSelectAccordion={onSelectAccordion}
                    onClickWhenDisabled={onClickWhenDisabled}
                  />
                </div>
              )
              // } else if (filter.title === TaxonomyType.ProductType || filter.title === TaxonomyType.Topic) {
            } else if (filter.title === TaxonomyType.CustomTag) {
              return (
                <div key={filter.title}>
                  <hr />
                  <CustomTagFilter
                    opened={opened}
                    onSelectAccordion={() => onSelectAccordion(TaxonomyType.CustomTag)}
                    onTagsChanged={onCustomTagsChanged ?? (() => {})}
                    sharedPortfolioId={sharedPortfolioId}
                    isSharedPortfolio={isSharedPortfolio}
                  />
                </div>
              )
            } else if (filter.title === TaxonomyType.ProductType) {
              return (
                <div key={filter?.title}>
                  <hr></hr>
                  <TopicList
                    key={`${filter.title}-${index}`}
                    title={TaxonomyType.ProductType}
                    items={filter.data}
                    opened={opened}
                    disabled={filtersDisabled}
                    showCheckbox
                    onSelectAccordion={onSelectAccordion}
                    onClickWhenDisabled={onClickWhenDisabled}
                  />
                </div>
              )
            } else if (filter.title === TaxonomyType.Topic) {
              return (
                <div key={filter?.title}>
                  <hr></hr>
                  <TopicList
                    key={`${filter.title}-${index}`}
                    title={TaxonomyType.Topic}
                    items={filter.data}
                    opened={opened}
                    disabled={filtersDisabled}
                    showCheckbox
                    onSelectAccordion={onSelectAccordion}
                    onClickWhenDisabled={onClickWhenDisabled}
                  />
                </div>
              )
            } else if (filter.title === TaxonomyType.Date) {
              return (
                <div key={filter?.title}>
                  <hr></hr>
                  <DateRangePicker
                    title={TaxonomyType.Date}
                    items={filter.data}
                    opened={opened}
                    disabled={filtersDisabled}
                    showCheckbox
                    onSelectAccordion={onSelectAccordion}
                    onClickWhenDisabled={onClickWhenDisabled}
                  />
                </div>
              )
            } else {
              return (
                <div key={filter?.title}>
                  <hr></hr>
                  <Accordion
                    key={`${filter.title}-${index}`}
                    title={filter.title}
                    items={filter.data}
                    opened={opened}
                    disabled={filtersDisabled}
                    showCheckbox
                    showSearchbox={[TaxonomyType.Integrations]}
                    onSelectAccordion={onSelectAccordion}
                    onClickWhenDisabled={onClickWhenDisabled}
                  />
                </div>
              )
            }
          })}
        </div>
      </div>
    )
  },
)
